import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Button as ReferaButton, DatePicker, Loader } from '@refera/ui-web'
import Button from '_components/button'
import Accordion from '_/components/accordion'
import { Select, DateTimePicker, BasicInput, ControlledSwitch } from '_/components/inputs'
import * as Input from '_/components/inputs/Input'
import { useToast } from '_/hooks/use-toast'
import SwitchComponent from '_/components/switch'
import {
  Select as MuiSelect,
  MenuItem,
  InputLabel,
  Typography,
  IconButton,
  InputAdornment,
  Tooltip,
  Grid,
} from '@material-ui/core'
import { Controller, FormProvider, useForm, useFieldArray } from 'react-hook-form'
import { FormHelperText, TextField } from '@mui/material'
import { userSelector } from '_/modules/authentication/selectors'
import { businessFrontListSelector } from '_/modules/businessFront/selectors'
import {
  CONTRACT_STATUS_OPTIONS,
  CONTRACT_TYPE_OPTIONS,
  AGENCY_REFERA_DETAILS_FIELDS,
  transformValue,
  FORBIDDEN_DEFINE_RESPONSIBLE_AREA_OF_ACTIVITY,
} from '../components/AgencyReferaDetailsModal/constants'
import { navigate, useParams } from '@reach/router'
import HeaderTitle from '_components/header-title'
import { agencySelector } from '_/modules/agency/selectors'
import { getConnectionSelector } from '_modules/provider/selectors'
import { getAgency, updateAgency } from '_/modules/agency/actions'
import { getBusinessFronts } from '_/modules/businessFront/actions'
import { getConnetionMessageTool } from '_/modules/provider/actions'
import {
  METHOD_PAYMENT_OPTIONS_SELECT,
  REFERA_CHARGE_METHODS_SELECT,
} from '_/views/finance/manage-installments/utils/constants'
import { useDialog } from '_/hooks/use-dialog'
import AccordionInfo from '_/components/accordion/accordion-info'
import ServiceAreasModal from '_/views/inspection-budget/components/modals/service-areas-modal'

import CheckedMultiSelect from '_/components/checked-multi-select'
import HelpOutlineOutlinedIcon from '@material-ui/icons/HelpOutlineOutlined'
import ClearIcon from '@material-ui/icons/Clear'
import moment from 'moment'
import useStyles from './styles'
import { areasOfActivityListSelector } from '_/modules/areaOfActivity/selectors'
import { getAreaOfActivityList } from '_/modules/areaOfActivity/actions'
import { getCities } from '_/modules/utils/actions'
import { formatErrorMessage } from '_/views/finance/utils/FormatErrorMessage'
import { getCitiesSelector } from '_modules/utils/selectors'
import Attachments from '_components/refera-ui/Attachments'
import PreviewComponent from '_components/common/preview-component'
import { Trash } from '@refera/ui-icons'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'

const AUTO_TRIAGE_DISABLED_MESSAGE =
  'Funcionalidade desabilitada! Para voltar a utilizá-la, contacte o time de produto.'

const agencyReferaDetailsFieldTypes = Object.entries(AGENCY_REFERA_DETAILS_FIELDS).reduce(
  (acc, [, value]) => {
    acc[value.name] = value.type
    return acc
  },
  {}
)

const transformPayloadValues = data => {
  const transformedData = Object.entries(data).reduce((acc, [key, value]) => {
    switch (agencyReferaDetailsFieldTypes[key]) {
      case 'number': {
        if (value === null || value === undefined || value === '') {
          acc[key] = null
          break
        }
        let numberValue
        if (typeof value === 'string') {
          // Remove R$ and spaces, replace comma with dot, remove thousand separator
          const cleanValue = value
            .replace('R$', '')
            .trim()
            .replace(/\.(?=\d{3},)/g, '')
            .replace(',', '.')

          numberValue = parseFloat(cleanValue)
        } else {
          numberValue = value
        }

        if (isNaN(numberValue)) {
          acc[key] = null
          break
        }

        acc[key] = numberValue.toFixed(2).toString()
        break
      }
      case 'datetime': {
        try {
          if (!value) {
            acc[key] = null
            break
          }

          const formattedDate = moment(value).toISOString()
          acc[key] = formattedDate
        } catch (error) {
          throw new Error('Data inválida')
        }
        break
      }
      case 'date': {
        try {
          if (!value) {
            acc[key] = null
            break
          }
          const formattedDate = moment(value).format('YYYY-MM-DD')
          acc[key] = formattedDate
        } catch (error) {
          throw new Error('Data inválida')
        }
        break
      }
      case 'multiselect': {
        acc[key] = value.map(item => ({ name: item.value }))

        break
      }
      default: {
        acc[key] = value
        break
      }
    }

    return acc
  }, {})
  return transformedData
}

const getAgencyCityExceptionList = agencyData =>
  agencyData?.insuranceCityExceptionList ?? agencyData?.insurance_city_exception_list ?? []

const mapCityExceptionToRegion = (cityException, cityOptions = []) => {
  if (!cityException) return null

  if (typeof cityException === 'string') {
    const matchedCity = cityOptions.find(
      city => city.value === cityException || city.label === cityException
    )

    if (matchedCity) {
      return {
        state: matchedCity.state.name,
        city: matchedCity.label,
      }
    }

    return {
      state: '',
      city: cityException,
    }
  }

  if (cityException.city && cityException.state) {
    return {
      state: cityException.state,
      city: cityException.city,
    }
  }

  const cityReference =
    cityException.city || cityException.citySlug || cityException.slug || cityException.value

  const matchedCity = cityOptions.find(
    city => city.value === cityReference || city.label === cityReference
  )

  if (matchedCity) {
    return {
      state: matchedCity.state.name,
      city: matchedCity.label,
    }
  }

  if (!cityReference) return null

  return {
    state: cityException.state || cityException.uf || '',
    city: cityReference,
  }
}

const buildCityExceptionRegions = (agencyData, cityOptions = []) =>
  getAgencyCityExceptionList(agencyData)
    .map(cityException => mapCityExceptionToRegion(cityException, cityOptions))
    .filter(region => region?.city)

const buildCityExceptionSlugs = (regions = [], cityOptions = []) =>
  regions.reduce((acc, region) => {
    const matchedCity = cityOptions.find(city => {
      const isSameCity = city.label === region?.city
      const isSameState =
        !region?.state || city.state.name === region.state || city.state.uf === region.state

      return isSameCity && isSameState
    })

    if (matchedCity?.value) {
      acc.push(matchedCity.value)
    }

    return acc
  }, [])

const ReferaDataScreen = () => {
  const areasOfActivityList = useSelector(areasOfActivityListSelector)
  const styles = useStyles()
  const agency = useSelector(agencySelector)?.toJS()
  const dispatch = useDispatch()
  const { agencyId } = useParams()
  const connectionsList = useSelector(getConnectionSelector)
  const user = useSelector(userSelector)
  const businessFrontList = useSelector(businessFrontListSelector)
  const cities = useSelector(getCitiesSelector)
  const { showToast } = useToast()
  const [selectedWhatsapp, setSelectedWhatsapp] = useState()
  const { showDialog, closeDialog } = useDialog()
  const [attachments, setAttachments] = useState([])

  const [loading, setLoading] = useState(false)
  const [isCityExceptionsModalOpen, setIsCityExceptionsModalOpen] = useState(false)
  const [editingCityExceptionIndex, setEditingCityExceptionIndex] = useState(null)
  const [cityExceptions, setCityExceptions] = useState([])
  const [agencyResponse, setAgencyResponse] = useState(null)
  const [hasUserTouchedCityExceptions, setHasUserTouchedCityExceptions] = useState(false)

  const handleBackNavigation = useCallback(() => {
    navigate(-1)
  }, [])

  const renderInput = useCallback(params => <TextField variant="standard" {...params} />, [])
  const whatsappConnectionOptions = useMemo(
    () => connectionsList?.slice()?.sort((a, b) => a.id - b.id) || [],
    [connectionsList]
  )

  const defaultValues = useMemo(() => {
    if (!agency) {
      return {}
    }

    const filteredValues = Object.entries(AGENCY_REFERA_DETAILS_FIELDS).reduce(
      (acc, [key, value]) => {
        if (
          key === 'AUTO_MESSAGING_SAAS' &&
          agency?.contractType &&
          agency.contractType !== 'SAAS'
        ) {
          acc[value.name] = false
          return acc
        }
        acc[value.name] = transformValue(agency[value.name], key)
        return acc
      },
      {}
    )
    return filteredValues
  }, [agency])

  useEffect(() => {
    if (!businessFrontList || businessFrontList.size === 0) {
      dispatch(getBusinessFronts())
    }
  }, [dispatch])

  useEffect(() => {
    if (agencyId) {
      dispatch(
        getConnetionMessageTool({
          purpose: 'seller',
        })
      )
    }
  }, [agencyId, dispatch])

  useEffect(() => {
    if (!areasOfActivityList?.length) {
      dispatch(getAreaOfActivityList())
    }
  }, [])

  const defaultPhone = useMemo(() => {
    const sellerConnectionId = defaultValues[AGENCY_REFERA_DETAILS_FIELDS.SELLER_CONNECTION.name]
    const connection = whatsappConnectionOptions.find(option => option.id === sellerConnectionId)
    return connection?.phone || ''
  }, [defaultValues, whatsappConnectionOptions])

  const flowOptions = useMemo(() => {
    return (
      businessFrontList
        ?.slice()
        .sort((a, b) => (a?.name || '').localeCompare(b?.name || ''))
        .map(flow => ({
          value: flow.name,
          label: flow.description,
        })) || []
    )
  }, [businessFrontList])

  const cityOptions = useMemo(
    () =>
      cities
        ?.valueSeq()
        ?.toArray()
        ?.sort((a, b) => (a?.name || '').localeCompare(b?.name || ''))
        ?.map(value => ({
          label: value.name,
          value: value.slug,
          state: value.state,
        })) || [],
    [cities]
  )

  useEffect(() => {
    if (!cityOptions?.length) {
      dispatch(getCities())
    }
  }, [cityOptions?.length, dispatch])

  const cityExceptionStateOptions = useMemo(() => {
    const uniqueStates = []
    cityOptions?.forEach(city => {
      if (!uniqueStates.find(state => state.id === city.state.id)) {
        uniqueStates.push({ id: city.state.id, name: city.state.name })
      }
    })
    return uniqueStates
  }, [cityOptions])

  const cityExceptionStateNames = useMemo(
    () => cityExceptionStateOptions.map(state => state.name).sort((a, b) => a.localeCompare(b)),
    [cityExceptionStateOptions]
  )

  const methods = useForm({
    mode: 'all',
    defaultValues: {
      [AGENCY_REFERA_DETAILS_FIELDS.SELLER_CONNECTION.name]: defaultPhone,
      [AGENCY_REFERA_DETAILS_FIELDS.IS_VIP_AGENCY.name]: agency?.isVipAgency || false,
      [AGENCY_REFERA_DETAILS_FIELDS.WARRANTY_COMPENSATION.name]:
        agency?.warrantyCompensation || false,
      [AGENCY_REFERA_DETAILS_FIELDS.PILOT_AUTO_TICKET.name]: agency?.pilotAutoTicket || false,
      [AGENCY_REFERA_DETAILS_FIELDS.ALLOWS_RENT_DISCOUNT.name]: agency?.allowsRentDiscount || false,
      [AGENCY_REFERA_DETAILS_FIELDS.DEFINE_RESPONSIBLE.name]: agency?.defineResponsible || false,
      [AGENCY_REFERA_DETAILS_FIELDS.SEND_NOTIFICATION.name]: agency?.sendNotification || false,
      [AGENCY_REFERA_DETAILS_FIELDS.PAYMENT_METHODS.name]: agency?.paymentMethods,
      [AGENCY_REFERA_DETAILS_FIELDS.REVIEW_BUDGET.name]: agency?.reviewBudget || false,
      [AGENCY_REFERA_DETAILS_FIELDS.INTERMEDIARY_VALIDATE_BUDGET.name]:
        agency?.intermediaryValidateBudget || false,
      [AGENCY_REFERA_DETAILS_FIELDS.SEND_TO_PAYER.name]: agency?.sendToPayer || false,
      [AGENCY_REFERA_DETAILS_FIELDS.AUTO_TRIAGE.name]: false,
      [AGENCY_REFERA_DETAILS_FIELDS.AUTO_MESSAGING_SAAS.name]:
        agency?.contractType === 'SAAS' ? agency?.autoMessagingSaas || false : false,
      [AGENCY_REFERA_DETAILS_FIELDS.TRIAGE_AIS.name]: agency?.triageAis || false,
      [AGENCY_REFERA_DETAILS_FIELDS.BUDGET_COVER]: agency?.budgetCover || false,
      [AGENCY_REFERA_DETAILS_FIELDS.AI_TEXT_CORRECTION.name]: agency?.aiTextCorrection || false,
      [AGENCY_REFERA_DETAILS_FIELDS.AI_PRICE_CONFERENCE.name]: agency?.aiPriceConference || false,
      [AGENCY_REFERA_DETAILS_FIELDS.HANDLES_INSPECTION.name]: agency?.handlesInspection || false,
      [AGENCY_REFERA_DETAILS_FIELDS.INSPECTION_PERCENTAGES.name]:
        agency?.inspectionPercentages?.length > 0
          ? agency?.inspectionPercentages
          : [{ referaPercentage: 0, agencyPercentage: 0 }],
      [AGENCY_REFERA_DETAILS_FIELDS.INSURANCE_CITY_EXCEPTION_LIST.name]: [],
      [AGENCY_REFERA_DETAILS_FIELDS.USE_EXTENDED_WARRANTY.name]:
        agency?.useExtendedWarranty || false,
    },
  })

  const {
    handleSubmit,
    control,
    reset,
    setValue,
    watch,
    register,
    formState: { dirtyFields },
  } = methods

  const { fields, append, remove } = useFieldArray({
    control,
    shouldUnregister: true,
    name: AGENCY_REFERA_DETAILS_FIELDS.INSPECTION_PERCENTAGES.name,
  })

  useEffect(() => {
    register(AGENCY_REFERA_DETAILS_FIELDS.INSURANCE_CITY_EXCEPTION_LIST.name)
  }, [register])

  useEffect(() => {
    if (!agencyResponse || hasUserTouchedCityExceptions) return

    setCityExceptions(buildCityExceptionRegions(agencyResponse, cityOptions))
  }, [agencyResponse, cityOptions, hasUserTouchedCityExceptions])

  const sortedCityExceptionFields = useMemo(
    () =>
      cityExceptions
        .map((item, originalIndex) => ({ ...item, originalIndex }))
        .sort((a, b) => a.state.localeCompare(b.state) || a.city.localeCompare(b.city)),
    [cityExceptions]
  )

  const watchContractType = watch(AGENCY_REFERA_DETAILS_FIELDS.CONTRACT_TYPE.name)
  const watchBudgetCover = watch(AGENCY_REFERA_DETAILS_FIELDS.BUDGET_COVER.name)
  const watchAreaOfActivity = watch(AGENCY_REFERA_DETAILS_FIELDS.AREA_OF_ACTIVITY.name)
  const handlesInspection = watch(AGENCY_REFERA_DETAILS_FIELDS.HANDLES_INSPECTION.name)
  const hasOrderWithoutPrice = !!agency?.flows?.find(
    flow => flow?.name === 'service_order_without_price'
  )

  const handleAreaChange = value => {
    showDialog({
      type: 'warning',
      subject: 'Você quer mudar os fluxos disponibilizados, conforme esta nova Área de atuação?',
      labelApprove: 'Sim',
      labelCancel: 'Não',
      onApprove: () => {
        const result = businessFrontList
          .filter(item => {
            if (!item.defaultAreaOfActivity) return false
            return item.defaultAreaOfActivity.split(',').includes(value)
          })
          .map(item => ({
            value: item.name,
            label: item.description,
          }))
        methods.setValue(AGENCY_REFERA_DETAILS_FIELDS.FLOWS.name, result)
        closeDialog()
      },
      onCancel: closeDialog,
    })
  }

  useEffect(() => {
    if (dirtyFields?.[AGENCY_REFERA_DETAILS_FIELDS.AREA_OF_ACTIVITY.name] && watchAreaOfActivity) {
      handleAreaChange(watchAreaOfActivity)
    }
  }, [watchAreaOfActivity, dirtyFields])

  useEffect(() => {
    setValue(
      AGENCY_REFERA_DETAILS_FIELDS.INSURANCE_CITY_EXCEPTION_LIST.name,
      buildCityExceptionSlugs(cityExceptions, cityOptions)
    )
  }, [cityExceptions, cityOptions, setValue])

  const getAgencyData = useCallback(() => {
    if (!agencyId) return
    dispatch(getAgency(agencyId, { bypass_cache: true })).then(response => {
      const normalizedResponse = {
        ...response,
        [AGENCY_REFERA_DETAILS_FIELDS.AUTO_TRIAGE.name]: false,
        inspectionPercentages:
          response?.inspectionPercentages?.length > 0
            ? response.inspectionPercentages
            : [{ referaPercentage: 0, agencyPercentage: 0 }],
      }
      setAgencyResponse(response)
      setHasUserTouchedCityExceptions(false)
      reset(normalizedResponse)
    })
  }, [agencyId, dispatch, reset])

  const handleSubmitData = useCallback(
    async data => {
      setLoading(true)
      let newData = {
        ...data,
        [AGENCY_REFERA_DETAILS_FIELDS.AUTO_TRIAGE.name]: false,
      }
      methods.setValue(AGENCY_REFERA_DETAILS_FIELDS.AUTO_TRIAGE.name, false)

      if (data?.contractType === 'SAAS') {
        newData = {
          ...newData,
          intermediaryValidateBudget: false,
          autoTriage: false,
          triageAis: false,
          useExtendedWarranty: false,
        }
        methods.setValue(AGENCY_REFERA_DETAILS_FIELDS.INTERMEDIARY_VALIDATE_BUDGET.name, false)
        methods.setValue(AGENCY_REFERA_DETAILS_FIELDS.AUTO_TRIAGE.name, false)
        methods.setValue(AGENCY_REFERA_DETAILS_FIELDS.TRIAGE_AIS.name, false)
        methods.setValue(AGENCY_REFERA_DETAILS_FIELDS.AI_TEXT_CORRECTION.name, false)
        methods.setValue(AGENCY_REFERA_DETAILS_FIELDS.AI_PRICE_CONFERENCE.name, false)
        methods.setValue(AGENCY_REFERA_DETAILS_FIELDS.USE_EXTENDED_WARRANTY.name, false)
      }
      if (data?.contractType !== 'SAAS') {
        methods.setValue(AGENCY_REFERA_DETAILS_FIELDS.REVIEW_BUDGET.name, true)
        methods.setValue(AGENCY_REFERA_DETAILS_FIELDS.SEND_TO_PAYER.name, true)
        methods.setValue(AGENCY_REFERA_DETAILS_FIELDS.USE_POOL_FOR_WHATSAPP.name, true)
        methods.setValue(AGENCY_REFERA_DETAILS_FIELDS.AUTO_MESSAGING_SAAS.name, false)

        newData = {
          ...newData,
          reviewBudget: true,
          sendToPayer: true,
          usePoolForWhatsapp: true,
          autoMessagingSaas: false,
        }
      }

      if (methods.getValues(AGENCY_REFERA_DETAILS_FIELDS.TRIAGE_AIS.name) === false) {
        methods.setValue(AGENCY_REFERA_DETAILS_FIELDS.AUTO_TRIAGE.name, false)
        newData = {
          ...newData,
          [AGENCY_REFERA_DETAILS_FIELDS.AUTO_TRIAGE.name]: false,
        }
      }

      let transformedPayload

      let budgetCoverPicture = null
      const [firstAttachment] = attachments
      if (firstAttachment instanceof File) budgetCoverPicture = firstAttachment

      try {
        const updatedData = {
          ...newData,
          isReferaData: true,
          budgetCoverPicture,
        }

        transformedPayload = transformPayloadValues(updatedData)
      } catch (error) {
        showToast({
          type: 'error',
        })
        setLoading(false)
        return
      }

      dispatch(updateAgency(transformedPayload, false))
        .then(() => {
          getAgencyData()
          showToast({
            type: 'success',
          })
          setLoading(false)
        })
        .catch(err => {
          showToast({
            type: 'error',
            message: formatErrorMessage(err),
          })
          setLoading(false)
        })
    },
    [showToast, dispatch, attachments]
  )

  const handleAutoTriageChange = useCallback(
    (event, callback) => {
      if (event.target.checked) {
        methods.setValue(AGENCY_REFERA_DETAILS_FIELDS.AUTO_TRIAGE.name, false, {
          shouldDirty: true,
        })
        showToast({
          type: 'warning',
          message: AUTO_TRIAGE_DISABLED_MESSAGE,
        })
        return
      }

      callback()
    },
    [methods, showToast]
  )

  function normalizeCpfCnpj(value) {
    return value.replace(/\D/g, '')
  }

  const handleServiceWithoutPriceFlow = useCallback(() => {
    const agencyCpfCnpj = agency?.cpfCnpj
    const regime = agency?.regime

    if (
      !agencyCpfCnpj ||
      (normalizeCpfCnpj(agencyCpfCnpj).length === 14 && (!regime || regime.trim() === ''))
    ) {
      showToast({
        type: 'error',
        message:
          'Para selecionar a opção "Ordem de serviço sem preço" no campo "Fluxo", você deve antes cadastrar o CNPF/CNPJ e Regime da empresa.',
      })
      return false
    }
    return true
  }, [agency, showToast])

  function shouldBlockBasedOnDefineResponsibleAndAreaOfActivity(data) {
    const defineResponsible = data?.[AGENCY_REFERA_DETAILS_FIELDS.DEFINE_RESPONSIBLE.name]

    if (!defineResponsible) return false

    const areaOfActivity = data?.[AGENCY_REFERA_DETAILS_FIELDS.AREA_OF_ACTIVITY.name]
    return FORBIDDEN_DEFINE_RESPONSIBLE_AREA_OF_ACTIVITY.includes(areaOfActivity)
  }

  const onSubmit = async data => {
    if (shouldBlockBasedOnDefineResponsibleAndAreaOfActivity(data)) {
      showToast({
        type: 'error',
        message:
          'Você não pode assinalar a opção "Definir responsável" em intermediárias "Incorporadoras", pois isto fará o chamado cair na etapa "Dados do pagador", gerando um erro.',
      })
      return
    }

    const changeTypeContractToSAAS =
      agency?.contractType !== 'SAAS' && data?.contractType === 'SAAS'

    const changeTypeContractToNotSAAS =
      agency?.contractType === 'SAAS' && data?.contractType !== 'SAAS'

    const hasSelectedOrderWithoutPriceFlow = !!data?.flows?.find(
      item => item?.value === 'service_order_without_price'
    )

    if (hasSelectedOrderWithoutPriceFlow && !hasOrderWithoutPrice) {
      const proceed = handleServiceWithoutPriceFlow()
      if (!proceed) return
    }

    if (changeTypeContractToSAAS || changeTypeContractToNotSAAS) {
      showDialog({
        type: 'warning',
        subject:
          'Esta ação irá mudar todos os perfis de acesso dos usuários desta intermediária, tornando-os todos administradores. Você confirma esta ação?',
        labelApprove: 'Sim',
        labelCancel: 'Não',
        onApprove: () => {
          handleSubmitData(data)
          closeDialog()
        },
        onCancel: closeDialog,
      })
    } else {
      handleSubmitData(data)
    }
  }

  const handleCancel = () => {
    if (Object.keys(dirtyFields).length > 0) {
      showDialog({
        type: 'warning',
        subject: 'Você tem certeza que deseja cancelar estas alterações?',
        labelApprove: 'Sim',
        labelCancel: 'Não',
        onApprove: () => {
          closeDialog()
          navigate(-1)
        },
        onCancel: closeDialog,
      })
    } else {
      navigate(-1)
    }
  }

  useEffect(() => {
    if (agencyId) getAgencyData()
  }, [agencyId, getAgencyData])

  useEffect(() => {
    if (agency?.budgetCoverPicture) {
      setAttachments([
        {
          id: agency?.id,
          file: agency?.budgetCoverPicture,
        },
      ])
    }
  }, [agency?.budgetCoverPicture])

  const renderContractStatusClearButton = useCallback(() => {
    if (!methods.getValues(AGENCY_REFERA_DETAILS_FIELDS.CONTRACT_STATUS.name)) return null

    return (
      <InputAdornment position="end">
        <IconButton
          onClick={() => methods.setValue(AGENCY_REFERA_DETAILS_FIELDS.CONTRACT_STATUS.name, null)}
          style={{ padding: '4px', marginRight: '20px' }}
        >
          <ClearIcon
            fontSize="small"
            style={{
              fill: '#B4B4B4',
            }}
          />
        </IconButton>
      </InputAdornment>
    )
  }, [methods.watch(AGENCY_REFERA_DETAILS_FIELDS.CONTRACT_STATUS.name)])

  const handleAddAttachment = useCallback(file => {
    setAttachments([...file])
  }, [])

  const handleRemoveAttachment = useCallback(
    index => {
      showDialog({
        subject: 'Você deseja deletar este item?',
        labelApprove: 'Sim',
        labelCancel: 'Não',
        type: 'info',
        onApprove: () => {
          setAttachments(prev => prev.filter((_, i) => i !== index))
          closeDialog()
        },
        onCancel: closeDialog,
      })
    },
    [showDialog, closeDialog]
  )

  const [previewState, setPreviewState] = useState({
    open: false,
    selectedIndex: undefined,
  })

  const handlePictureItemClick = index =>
    setPreviewState({
      open: true,
      selectedIndex: index,
    })

  const handleAppend = useCallback(() => {
    if (fields.length < 4) {
      append({ referaPercentage: 0, agencyPercentage: 0 })
    } else {
      showToast({
        type: 'warning',
        message: 'Você só pode inserir até 4 percentuais',
      })
    }
  }, [append, fields, showToast])

  const handleRemove = useCallback(
    index => {
      if (fields?.length > 1) {
        showDialog({
          type: 'warning',
          subject: 'Você tem certeza que deseja executar esta ação?',
          onApprove: () => {
            remove(index)
            closeDialog()
          },
          onCancel: closeDialog,
          labelApprove: 'Confirmar',
          labelCancel: 'Cancelar',
        })
      } else {
        showToast({
          type: 'warning',
          message: 'Você precisa ter pelo menos 1 percentual definido.',
        })
      }
    },
    [remove, fields, showDialog, closeDialog, showToast]
  )

  const openAddCityExceptionModal = useCallback(() => {
    setEditingCityExceptionIndex(null)
    setIsCityExceptionsModalOpen(true)
  }, [])

  const openEditCityExceptionModal = useCallback(index => {
    setEditingCityExceptionIndex(index)
    setIsCityExceptionsModalOpen(true)
  }, [])

  const handleCloseCityExceptionModal = useCallback(() => {
    setEditingCityExceptionIndex(null)
    setIsCityExceptionsModalOpen(false)
  }, [])

  const handleSubmitCityException = useCallback(
    cityException => {
      if (!cityException?.state || !cityException?.city) {
        return
      }

      const duplicateExceptionIndex = cityExceptions.findIndex(
        (item, index) =>
          item.city === cityException.city &&
          item.state === cityException.state &&
          index !== editingCityExceptionIndex
      )

      if (duplicateExceptionIndex !== -1) {
        showToast({
          type: 'warning',
          message: 'Esta cidade já foi adicionada.',
        })
        return
      }

      let updatedCityExceptions = []
      if (editingCityExceptionIndex !== null) {
        updatedCityExceptions = cityExceptions.map((item, index) =>
          index === editingCityExceptionIndex ? cityException : item
        )
      } else {
        updatedCityExceptions = [...cityExceptions, cityException]
      }

      setHasUserTouchedCityExceptions(true)
      setCityExceptions(updatedCityExceptions)
      handleCloseCityExceptionModal()
    },
    [cityExceptions, editingCityExceptionIndex, showToast, handleCloseCityExceptionModal]
  )

  const handleDeleteCityException = useCallback(
    index => {
      setHasUserTouchedCityExceptions(true)
      setCityExceptions(cityExceptions.filter((_, currentIndex) => currentIndex !== index))
    },
    [cityExceptions]
  )

  return (
    <FormProvider {...methods}>
      <Loader open={loading} hasBackdrop />
      <form className={styles.form} onSubmit={e => e.preventDefault()}>
        <HeaderTitle
          title={`Dados da Refera / Intermediária ${agency?.name || ''}`}
          backButtonAction={handleBackNavigation}
        >
          <Grid className={styles.headerButtons}>
            {methods.watch(AGENCY_REFERA_DETAILS_FIELDS.CONTRACT_STATUS.name) !== 'cancelled' && (
              <Button
                className={styles.button}
                style={{ backgroundColor: 'red', color: 'white' }}
                variant="ghost"
                onClick={() => {}}
              >
                Solicitar rescisão
              </Button>
            )}
            <ReferaButton
              onClick={handleCancel}
              className={styles.button}
              color="red"
              variant="secondary"
            >
              Cancelar
            </ReferaButton>
            <Button
              className={styles.button}
              onClick={handleSubmit(onSubmit)}
              disabled={loading}
              color="primary"
              variant="contained"
            >
              Salvar
            </Button>
          </Grid>
        </HeaderTitle>
        <Grid className={styles.container}>
          <Accordion title="Informações de venda" className={styles.accordion}>
            <div className={styles.column}>
              <div className={styles.row}>
                <Select
                  label="Tipo de contrato"
                  name={AGENCY_REFERA_DETAILS_FIELDS.CONTRACT_TYPE.name}
                  options={CONTRACT_TYPE_OPTIONS}
                  getLabel={item => item.label}
                  getValue={item => item.value}
                  defaultValue={agency?.contractType}
                  disabled={user?.activeProfile?.name === 'intermediary'}
                />
              </div>
            </div>
            <div className={styles.column}>
              <div className={styles.row}>
                <Select
                  label="Área de atuação"
                  name={AGENCY_REFERA_DETAILS_FIELDS.AREA_OF_ACTIVITY.name}
                  options={
                    areasOfActivityList?.filter(
                      item => item.active || item.name === agency?.areaOfActivity
                    ) || []
                  }
                  getLabel={item => item.name}
                  getValue={item => item.name}
                  required
                  defaultValue={
                    defaultValues[AGENCY_REFERA_DETAILS_FIELDS.AREA_OF_ACTIVITY.name] ??
                    agency?.areaOfActivity ??
                    ''
                  }
                />
              </div>
            </div>
            <div className={styles.column}>
              <div className={styles.row}>
                <Select
                  label="Status do contrato"
                  name={AGENCY_REFERA_DETAILS_FIELDS.CONTRACT_STATUS.name}
                  defaultValue={
                    defaultValues[AGENCY_REFERA_DETAILS_FIELDS.CONTRACT_STATUS.name] ?? null
                  }
                  getLabel={item => item.label}
                  getValue={item => item.value}
                  endAdornment={renderContractStatusClearButton()}
                  options={CONTRACT_STATUS_OPTIONS}
                  disabled={user?.activeProfile?.name === 'intermediary'}
                />
              </div>
            </div>
            <DateTimePicker
              label="Data de geracão do contrato"
              name={AGENCY_REFERA_DETAILS_FIELDS.CONTRACT_GENERATION_DATE.name}
              defaultValue={
                defaultValues[AGENCY_REFERA_DETAILS_FIELDS.CONTRACT_GENERATION_DATE.name] ?? null
              }
              rules={{}}
              className={styles.dateTimePicker}
              referaDatePickerProps={{ disabled: true }}
            />
            <div className={styles.datePickerInputGroup}>
              <InputLabel
                variant="standard"
                id={`${AGENCY_REFERA_DETAILS_FIELDS.CONTRACT_SIGNING_DATE.name}-label`}
                shrink
                className={styles.datePickerLabel}
              >
                Data de assinatura do contrato
              </InputLabel>
              <Controller
                control={methods.control}
                name={AGENCY_REFERA_DETAILS_FIELDS.CONTRACT_SIGNING_DATE.name}
                defaultValue={
                  defaultValues[AGENCY_REFERA_DETAILS_FIELDS.CONTRACT_SIGNING_DATE.name] ?? null
                }
                render={field => {
                  return (
                    <DatePicker
                      variant="inline"
                      mask="__/__/____"
                      format="dd/MM/yyyy"
                      refuse={/[^\d.]+/gi}
                      renderInput={renderInput}
                      disabled
                      {...field}
                    />
                  )
                }}
              />
              <FormHelperText
                className={styles.errorMessage}
                error={!!methods.errors?.contractSignatureDate}
              >
                {methods.errors?.contractSignatureDate?.message}
              </FormHelperText>
            </div>
            <DateTimePicker
              label="Data de bloqueio por contrato"
              name={AGENCY_REFERA_DETAILS_FIELDS.CONTRACT_BLOCK_DATE.name}
              defaultValue={
                defaultValues[AGENCY_REFERA_DETAILS_FIELDS.CONTRACT_BLOCK_DATE.name] ?? null
              }
              rules={{}}
              className={styles.dateTimePicker}
              referaDatePickerProps={{ disabled: user?.activeProfile?.name === 'intermediary' }}
            />
            <div className={styles.switchInputGroup}>
              <label
                className={styles.switchLabel}
                htmlFor={AGENCY_REFERA_DETAILS_FIELDS.IS_VIP_AGENCY.name}
              >
                Agência VIP
              </label>
              <Controller
                control={methods.control}
                id={AGENCY_REFERA_DETAILS_FIELDS.IS_VIP_AGENCY.name}
                name={AGENCY_REFERA_DETAILS_FIELDS.IS_VIP_AGENCY.name}
                defaultValue={defaultValues[AGENCY_REFERA_DETAILS_FIELDS.IS_VIP_AGENCY.name]}
                render={props => (
                  <SwitchComponent
                    checked={props.value}
                    onChange={e => props.onChange(e.target.checked)}
                  />
                )}
              />
            </div>

            <Input.Root
              name={AGENCY_REFERA_DETAILS_FIELDS.VIP_LIMIT.name}
              defaultValue={defaultValues[AGENCY_REFERA_DETAILS_FIELDS.VIP_LIMIT.name]}
            >
              <Input.Label
                labelClasses={styles.labelNumberInput}
                name={AGENCY_REFERA_DETAILS_FIELDS.VIP_LIMIT}
              >
                Limite de VIPs
              </Input.Label>
              <Input.ControllerNumber
                name={AGENCY_REFERA_DETAILS_FIELDS.VIP_LIMIT.name}
                decimalScale={0}
              />
              <Input.ErrorMessage name={AGENCY_REFERA_DETAILS_FIELDS.VIP_LIMIT.name} />
            </Input.Root>

            <br />

            <div className={styles.column} style={{ gap: '3rem' }}>
              <div className={styles.row}>
                <Input.Root
                  name={AGENCY_REFERA_DETAILS_FIELDS.MINIMUM_SALE_VALUE.name}
                  defaultValue={defaultValues[AGENCY_REFERA_DETAILS_FIELDS.MINIMUM_SALE_VALUE.name]}
                >
                  <Input.Label
                    labelClasses={styles.labelNumberInput}
                    name={AGENCY_REFERA_DETAILS_FIELDS.MINIMUM_SALE_VALUE.name}
                  >
                    Valor mínimo para venda
                  </Input.Label>
                  <Input.ControllerNumber
                    prefix="R$ "
                    name={AGENCY_REFERA_DETAILS_FIELDS.MINIMUM_SALE_VALUE.name}
                  />
                  <Input.ErrorMessage name={AGENCY_REFERA_DETAILS_FIELDS.MINIMUM_SALE_VALUE.name} />
                </Input.Root>
              </div>

              <div className={styles.row}>
                <div className={styles.switchInputGroup}>
                  <ControlledSwitch
                    label={
                      <div className={styles.switchRentLabel}>Inclusão de capa nos orçamentos</div>
                    }
                    name={AGENCY_REFERA_DETAILS_FIELDS.BUDGET_COVER.name}
                    defaultValue={defaultValues[AGENCY_REFERA_DETAILS_FIELDS.BUDGET_COVER.name]}
                    containerClasses={styles.checkboxContainer}
                  />
                </div>
              </div>
            </div>
            <div className={styles.column}>
              {watchBudgetCover && (
                <div className={styles.row} style={{ marginTop: '117px' }}>
                  <PreviewComponent
                    images={attachments}
                    {...previewState}
                    setPreviewState={setPreviewState}
                  />
                  <div className={styles.switchInputGroup}>
                    <Input.Label
                      labelClasses={styles.labelNumberInput}
                      name={AGENCY_REFERA_DETAILS_FIELDS.MINIMUM_SALE_VALUE.name}
                    >
                      Capa de orçamentos
                    </Input.Label>
                    <Attachments
                      content="images"
                      files={attachments}
                      multiple={false}
                      accept={{
                        'image/jpeg': ['.jpg'],
                        'image/png': ['.png'],
                      }}
                      onDrop={handleAddAttachment}
                      onRemoveItem={handleRemoveAttachment}
                      onItemClick={handlePictureItemClick}
                    />
                  </div>
                </div>
              )}
            </div>
          </Accordion>

          <Accordion title="Informações Financeiras" className={styles.accordion2}>
            <Input.Root
              name={AGENCY_REFERA_DETAILS_FIELDS.AGENCY_COMPLETION_PERCENTAGE.name}
              defaultValue={
                defaultValues[AGENCY_REFERA_DETAILS_FIELDS.AGENCY_COMPLETION_PERCENTAGE.name]
              }
            >
              <Input.Label
                labelClasses={styles.labelNumberInput}
                name={AGENCY_REFERA_DETAILS_FIELDS.AGENCY_COMPLETION_PERCENTAGE.name}
              >
                Percentual da intermediária
              </Input.Label>
              <Input.ControllerNumber
                name={AGENCY_REFERA_DETAILS_FIELDS.AGENCY_COMPLETION_PERCENTAGE.name}
                decimalScale={2}
              />
              <Input.ErrorMessage
                name={AGENCY_REFERA_DETAILS_FIELDS.AGENCY_COMPLETION_PERCENTAGE.name}
              />
            </Input.Root>

            <Input.Root
              name={AGENCY_REFERA_DETAILS_FIELDS.REFERA_COMPLETION_PERCENTAGE.name}
              defaultValue={
                defaultValues[AGENCY_REFERA_DETAILS_FIELDS.REFERA_COMPLETION_PERCENTAGE.name]
              }
            >
              <Input.Label
                labelClasses={styles.labelNumberInput}
                name={AGENCY_REFERA_DETAILS_FIELDS.REFERA_COMPLETION_PERCENTAGE.name}
              >
                Percentual da Refera
              </Input.Label>
              <Input.ControllerNumber
                name={AGENCY_REFERA_DETAILS_FIELDS.REFERA_COMPLETION_PERCENTAGE.name}
                decimalScale={2}
              />
              <Input.ErrorMessage
                name={AGENCY_REFERA_DETAILS_FIELDS.REFERA_COMPLETION_PERCENTAGE.name}
              />
            </Input.Root>
            <div style={{ marginTop: '-16px' }}>
              <CheckedMultiSelect
                control={control}
                name="paymentMethods"
                label="Formas de pagamento"
                defaultValue={agency?.paymentMethods?.map(method => ({
                  value: method.status,
                  label: method.option,
                }))}
                options={METHOD_PAYMENT_OPTIONS_SELECT.filter(
                  option => option.label !== 'Selecione'
                )}
                style={{ marginTop: '2px' }}
              />
            </div>

            <Select
              control={control}
              name="referaChargeMethod"
              label="Forma de cobrança da Refera"
              getLabel={item => item.label}
              getValue={item => item.value}
              defaultValue={agency?.referaChargeMethod}
              options={REFERA_CHARGE_METHODS_SELECT.filter(option => option.label !== 'Selecione')}
            />

            <div className={styles.row}>
              <div className={styles.switchInputGroup}>
                <ControlledSwitch
                  label={
                    <div className={styles.switchRentLabel}>
                      Atende Vistoria
                      <Tooltip
                        arrow
                        disableFocusListener
                        disableTouchListener
                        title={
                          <Typography className={styles.tooltipText}>
                            Se ativado, permite a emissão de orçamentos de desocupação / vistoria
                          </Typography>
                        }
                        placement="bottom-end"
                      >
                        <HelpOutlineOutlinedIcon className={styles.tooltipIcon} />
                      </Tooltip>
                    </div>
                  }
                  name={AGENCY_REFERA_DETAILS_FIELDS.HANDLES_INSPECTION.name}
                  defaultValue={defaultValues[AGENCY_REFERA_DETAILS_FIELDS.HANDLES_INSPECTION.name]}
                  containerClasses={styles.checkboxContainer}
                />
              </div>
            </div>
            {handlesInspection &&
              fields?.map((field, index) => (
                <div key={field.id} className={styles.arrayContainer}>
                  <Input.Root
                    name={`${AGENCY_REFERA_DETAILS_FIELDS.INSPECTION_PERCENTAGES.name}.${index}.referaPercentage`}
                    defaultValue={
                      defaultValues[AGENCY_REFERA_DETAILS_FIELDS.INSPECTION_PERCENTAGES.name]?.[
                        index
                      ]?.referaPercentage ?? 0
                    }
                  >
                    <Input.Label
                      labelClasses={styles.labelNumberInput}
                      name={`${AGENCY_REFERA_DETAILS_FIELDS.INSPECTION_PERCENTAGES.name}.${index}.referaPercentage`}
                    >
                      Percentual Refera {index + 1}
                    </Input.Label>

                    <Input.ControllerNumber
                      name={`${AGENCY_REFERA_DETAILS_FIELDS.INSPECTION_PERCENTAGES.name}.${index}.referaPercentage`}
                      decimalScale={2}
                      customOnBlur={(value, { onChange }) => {
                        if (!value) {
                          onChange(0)
                        }
                      }}
                      rules={{
                        max: {
                          value: 100,
                          message: 'O valor máximo é 100',
                        },
                      }}
                    />

                    <Input.ErrorMessage
                      name={`${AGENCY_REFERA_DETAILS_FIELDS.INSPECTION_PERCENTAGES.name}.${index}.referaPercentage`}
                    />
                  </Input.Root>

                  <Input.Root
                    name={`${AGENCY_REFERA_DETAILS_FIELDS.INSPECTION_PERCENTAGES.name}.${index}.agencyPercentage`}
                    defaultValue={
                      defaultValues[AGENCY_REFERA_DETAILS_FIELDS.INSPECTION_PERCENTAGES.name]?.[
                        index
                      ]?.agencyPercentage ?? 0
                    }
                  >
                    <Input.Label
                      labelClasses={styles.labelNumberInput}
                      name={`${AGENCY_REFERA_DETAILS_FIELDS.INSPECTION_PERCENTAGES.name}.${index}.agencyPercentage`}
                    >
                      Percentual Intermediária {index + 1}
                    </Input.Label>

                    <Input.ControllerNumber
                      name={`${AGENCY_REFERA_DETAILS_FIELDS.INSPECTION_PERCENTAGES.name}.${index}.agencyPercentage`}
                      decimalScale={2}
                      customOnBlur={(value, { onChange }) => {
                        if (!value) {
                          onChange(0)
                        }
                      }}
                      rules={{
                        max: {
                          value: 100,
                          message: 'O valor máximo é 100',
                        },
                      }}
                    />

                    <Input.ErrorMessage
                      name={`${AGENCY_REFERA_DETAILS_FIELDS.INSPECTION_PERCENTAGES.name}.${index}.agencyPercentage`}
                    />
                  </Input.Root>
                  <div className={styles.row} style={{ gap: 2 }}>
                    <IconButton
                      variant="ghost"
                      className={styles.iconButton}
                      onClick={() => handleRemove(index)}
                    >
                      <Trash color="red" />
                    </IconButton>
                    {index === 0 && (
                      <IconButton
                        variant="ghost"
                        className={styles.iconButton}
                        onClick={handleAppend}
                      >
                        <AddCircleOutlineIcon
                          style={{ color: '#1671ED' }}
                          className={styles.editIcon}
                        />
                      </IconButton>
                    )}
                  </div>
                </div>
              ))}
          </Accordion>

          <Accordion title="Configuração de Termos" className={styles.accordion}>
            <div className={styles.column}>
              <BasicInput
                label={
                  <div className={styles.switchRentLabel}>
                    Termo de Proprietário
                    <Tooltip
                      title="Informe o termo, no singular, que será usado para substituir o termo 'Proprietário' no Formulário de abertura, Dados dos envolvidos, e outros locais. "
                      arrow
                    >
                      <HelpOutlineOutlinedIcon className={styles.tooltipIcon} />
                    </Tooltip>
                  </div>
                }
                name={AGENCY_REFERA_DETAILS_FIELDS.OWNER_TERM.name}
                defaultValue="Proprietário"
                required
                labelClasses={styles.label}
                rules={{
                  maxLength: {
                    value: 100,
                    message: `Esse campo aceita no máximo 100 caracteres`,
                  },
                }}
              />
            </div>
            <div className={styles.column}>
              <BasicInput
                label={
                  <div className={styles.switchRentLabel}>
                    Termo de Proprietários
                    <Tooltip
                      title="Informe o termo, no plural, que será usado para substituir o termo 'Proprietário' no Formulário de abertura, Dados dos envolvidos, e outros locais."
                      arrow
                    >
                      <HelpOutlineOutlinedIcon className={styles.tooltipIcon} />
                    </Tooltip>
                  </div>
                }
                name={AGENCY_REFERA_DETAILS_FIELDS.OWNERS_TERM.name}
                defaultValue="Proprietários"
                required
                labelClasses={styles.label}
                rules={{
                  maxLength: {
                    value: 100,
                    message: `Esse campo aceita no máximo 100 caracteres`,
                  },
                }}
              />
            </div>
            <br />
            <div className={styles.column}>
              <BasicInput
                label={
                  <div className={styles.switchRentLabel}>
                    Termo de Inquilino
                    <Tooltip
                      title="Informe o termo, no singular, que será usado para substituir o termo 'Inquilinos' no Formulário de abertura, Dados dos envolvidos, e outros locais."
                      arrow
                    >
                      <HelpOutlineOutlinedIcon className={styles.tooltipIcon} />
                    </Tooltip>
                  </div>
                }
                name={AGENCY_REFERA_DETAILS_FIELDS.TENANT_TERM.name}
                defaultValue="Inquilino"
                required
                labelClasses={styles.label}
                rules={{
                  maxLength: {
                    value: 100,
                    message: `Esse campo aceita no máximo 100 caracteres`,
                  },
                }}
              />
            </div>
            <div className={styles.column}>
              <BasicInput
                label={
                  <div className={styles.switchRentLabel}>
                    Termo de Inquilinos
                    <Tooltip
                      title="Informe o termo, no plural, que será usado para substituir o termo 'Inquilinos' no Formulário de abertura, Dados dos envolvidos, e outros locais."
                      arrow
                    >
                      <HelpOutlineOutlinedIcon className={styles.tooltipIcon} />
                    </Tooltip>
                  </div>
                }
                name={AGENCY_REFERA_DETAILS_FIELDS.TENANTS_TERM.name}
                defaultValue="Inquilinos"
                required
                labelClasses={styles.label}
                rules={{
                  maxLength: {
                    value: 100,
                    message: `Esse campo aceita no máximo 100 caracteres`,
                  },
                }}
              />
            </div>
            <br />
            <div className={styles.column}>
              <Grid className={styles.row}>
                <BasicInput
                  label={
                    <div className={styles.switchRentLabel}>
                      Termo de manutenção
                      <Tooltip
                        title="Informe o termo, no singular, que será utilizado para substituir o que a refera usa hoje como 'Manutenção' para se comunicar com clientes."
                        arrow
                      >
                        <HelpOutlineOutlinedIcon className={styles.tooltipIcon} />
                      </Tooltip>
                    </div>
                  }
                  name={AGENCY_REFERA_DETAILS_FIELDS.MAINTENANCE_TERM.name}
                  defaultValue="Manutenção"
                  labelClasses={styles.label}
                  rules={{
                    maxLength: {
                      value: 25,
                      message: `Esse campo aceita no máximo 25 caracteres`,
                    },
                  }}
                />
              </Grid>
            </div>
            <div className={styles.column}>
              <Grid className={styles.row}>
                <BasicInput
                  label={
                    <div className={styles.switchRentLabel}>
                      Termo de manutenções
                      <Tooltip
                        title="Informe o termo, no plural, que será utilizado para substituir o que a refera usa hoje como 'Manutenções' para se comunicar com clientes."
                        arrow
                      >
                        <HelpOutlineOutlinedIcon className={styles.tooltipIcon} />
                      </Tooltip>
                    </div>
                  }
                  name={AGENCY_REFERA_DETAILS_FIELDS.MAINTENANCES_TERM.name}
                  defaultValue="Manutenções"
                  labelClasses={styles.label}
                  rules={{
                    maxLength: {
                      value: 30,
                      message: `Esse campo aceita no máximo 30 caracteres`,
                    },
                  }}
                />
              </Grid>
            </div>
            <br />
            <div className={styles.column}>
              <BasicInput
                label="Termo dos empreendimentos"
                name={AGENCY_REFERA_DETAILS_FIELDS.PROPERTIES_TERM.name}
                defaultValue={agency?.propertiesTerm}
                labelClasses={styles.label}
                rules={{
                  maxLength: {
                    value: 20,
                    message: `Esse campo aceita no máximo 20 caracteres`,
                  },
                }}
              />
            </div>
            <div className={styles.column}>
              <BasicInput
                label="Termo do empreendimento"
                name={AGENCY_REFERA_DETAILS_FIELDS.PROPERTY_TERM.name}
                defaultValue={agency?.propertyTerm}
                labelClasses={styles.label}
                rules={{
                  maxLength: {
                    value: 20,
                    message: `Esse campo aceita no máximo 20 caracteres`,
                  },
                }}
              />
            </div>
          </Accordion>

          <Accordion title="Informações de Contato" className={styles.accordion}>
            {user?.activeProfile?.name === 'refera' && (
              <div className={styles.switchInputGroup}>
                <label className={styles.switchLabel}>Conexão do vendedor</label>
                <Controller
                  name={AGENCY_REFERA_DETAILS_FIELDS.SELLER_CONNECTION.name}
                  control={methods.control}
                  render={({ field }) => (
                    <MuiSelect
                      {...field}
                      value={
                        selectedWhatsapp ||
                        defaultValues[AGENCY_REFERA_DETAILS_FIELDS.SELLER_CONNECTION.name] ||
                        ''
                      }
                      onChange={e => {
                        const newValue = e.target.value
                        setSelectedWhatsapp(newValue)
                        methods.setValue(
                          AGENCY_REFERA_DETAILS_FIELDS.SELLER_CONNECTION.name,
                          newValue
                        )
                      }}
                    >
                      {whatsappConnectionOptions.map(option => (
                        <MenuItem key={option.id} value={option.id}>
                          {option.name} {option.phone}
                        </MenuItem>
                      ))}
                    </MuiSelect>
                  )}
                />
              </div>
            )}
          </Accordion>

          <Accordion title="Configurações adicionais" className={styles.accordion}>
            <div className={styles.switchInputGroup}>
              <label
                className={styles.switchLabel}
                htmlFor={AGENCY_REFERA_DETAILS_FIELDS.SEND_NOTIFICATION.name}
              >
                Enviar Notificação
              </label>
              <Controller
                control={methods.control}
                name={AGENCY_REFERA_DETAILS_FIELDS.SEND_NOTIFICATION.name}
                defaultValue={defaultValues[AGENCY_REFERA_DETAILS_FIELDS.SEND_NOTIFICATION.name]}
                render={props => (
                  <SwitchComponent
                    checked={props.value}
                    onChange={e => props.onChange(e.target.checked)}
                  />
                )}
              />
            </div>
            <div className={styles.switchInputGroup}>
              <label
                className={styles.switchLabel}
                htmlFor={AGENCY_REFERA_DETAILS_FIELDS.DEFINE_RESPONSIBLE.name}
              >
                Definir responsável
              </label>
              <Controller
                control={methods.control}
                name={AGENCY_REFERA_DETAILS_FIELDS.DEFINE_RESPONSIBLE.name}
                defaultValue={defaultValues[AGENCY_REFERA_DETAILS_FIELDS.DEFINE_RESPONSIBLE.name]}
                render={props => (
                  <SwitchComponent
                    checked={props.value}
                    onChange={e => props.onChange(e.target.checked)}
                  />
                )}
              />
            </div>
            <div className={styles.row}>
              <div className={styles.switchInputGroup}>
                <div className={styles.switchField}>
                  <label
                    className={styles.switchLabel}
                    htmlFor={AGENCY_REFERA_DETAILS_FIELDS.ALLOWS_RENT_DISCOUNT.name}
                  >
                    Comunica desconto no aluguel
                  </label>
                  <Tooltip
                    arrow
                    disableFocusListener
                    disableTouchListener
                    title={
                      <Typography className={styles.tooltipText}>
                        Configura a disponibilidade de escolha do desconto no aluguel na comunicação
                        com os clientes finais, sem afetar a exibição nos dados de pagamento do
                        orçamento no Backoffice.
                      </Typography>
                    }
                    placement="bottom-end"
                  >
                    <HelpOutlineOutlinedIcon className={styles.tooltipIcon} />
                  </Tooltip>
                </div>

                <Controller
                  control={methods.control}
                  name={AGENCY_REFERA_DETAILS_FIELDS.ALLOWS_RENT_DISCOUNT.name}
                  defaultValue={
                    defaultValues[AGENCY_REFERA_DETAILS_FIELDS.ALLOWS_RENT_DISCOUNT.name]
                  }
                  render={props => (
                    <SwitchComponent
                      checked={props.value}
                      onChange={e => props.onChange(e.target.checked)}
                    />
                  )}
                />
              </div>
            </div>
            <div className={styles.row}>
              <div className={styles.switchInputGroup}>
                <div className={styles.switchField}>
                  <label
                    className={styles.switchLabel}
                    htmlFor={AGENCY_REFERA_DETAILS_FIELDS.REVIEW_BUDGET.name}
                  >
                    Revisar orçamento
                  </label>
                  <Tooltip
                    arrow
                    disableFocusListener
                    disableTouchListener
                    title={
                      <Typography className={styles.tooltipText}>
                        Se ativado, o orçamento recebido ficará em uma etapa, aguardando a sua
                        revisão pela empresa, para só então seguir o fluxo.
                      </Typography>
                    }
                    placement="bottom-end"
                  >
                    <HelpOutlineOutlinedIcon className={styles.tooltipIcon} />
                  </Tooltip>
                </div>

                <Controller
                  control={methods.control}
                  name={AGENCY_REFERA_DETAILS_FIELDS.REVIEW_BUDGET.name}
                  defaultValue={defaultValues[AGENCY_REFERA_DETAILS_FIELDS.REVIEW_BUDGET.name]}
                  render={props => (
                    <SwitchComponent
                      checked={props.value}
                      onChange={e => props.onChange(e.target.checked)}
                      disabled={watchContractType !== 'SAAS'}
                    />
                  )}
                />
              </div>
            </div>
            <div className={styles.row}>
              <div className={styles.switchInputGroup}>
                <div className={styles.switchField}>
                  <label
                    className={styles.switchLabel}
                    htmlFor={AGENCY_REFERA_DETAILS_FIELDS.INTERMEDIARY_VALIDATE_BUDGET.name}
                  >
                    Validar orçamento
                  </label>
                  <Tooltip
                    arrow
                    disableFocusListener
                    disableTouchListener
                    title={
                      <Typography className={styles.tooltipText}>
                        Se ativado, o orçamento revisado ficará em uma etapa, aguardando a validação
                        pela intermediária, para só então seguir o fluxo.
                      </Typography>
                    }
                    placement="bottom-end"
                  >
                    <HelpOutlineOutlinedIcon className={styles.tooltipIcon} />
                  </Tooltip>
                </div>

                <Controller
                  control={methods.control}
                  name={AGENCY_REFERA_DETAILS_FIELDS.INTERMEDIARY_VALIDATE_BUDGET.name}
                  defaultValue={
                    defaultValues[AGENCY_REFERA_DETAILS_FIELDS.INTERMEDIARY_VALIDATE_BUDGET.name]
                  }
                  render={props => (
                    <SwitchComponent
                      checked={props.value}
                      onChange={e => props.onChange(e.target.checked)}
                      disabled={watchContractType === 'SAAS'}
                    />
                  )}
                />
              </div>
            </div>
            <div className={styles.row}>
              <div className={styles.switchInputGroup}>
                <div className={styles.switchField}>
                  <label
                    className={styles.switchLabel}
                    htmlFor={AGENCY_REFERA_DETAILS_FIELDS.SEND_TO_PAYER.name}
                  >
                    Enviar ao pagador
                  </label>
                  <Tooltip
                    arrow
                    disableFocusListener
                    disableTouchListener
                    title={
                      <Typography className={styles.tooltipText}>
                        Se ativado, o orçamento revisado ficará em uma etapa, aguardando o envio ao
                        pagador, para só então seguir o fluxo.
                      </Typography>
                    }
                    placement="bottom-end"
                  >
                    <HelpOutlineOutlinedIcon className={styles.tooltipIcon} />
                  </Tooltip>
                </div>

                <Controller
                  control={methods.control}
                  name={AGENCY_REFERA_DETAILS_FIELDS.SEND_TO_PAYER.name}
                  defaultValue={defaultValues[AGENCY_REFERA_DETAILS_FIELDS.SEND_TO_PAYER.name]}
                  render={props => (
                    <SwitchComponent
                      checked={props.value}
                      onChange={e => props.onChange(e.target.checked)}
                      disabled={watchContractType !== 'SAAS'}
                    />
                  )}
                />
              </div>
            </div>
            <div className={styles.switchInputGroup}>
              <label
                className={styles.switchLabel}
                htmlFor={AGENCY_REFERA_DETAILS_FIELDS.WARRANTY_COMPENSATION.name}
              >
                Compensação pela garantia
              </label>
              <Controller
                control={methods.control}
                name={AGENCY_REFERA_DETAILS_FIELDS.WARRANTY_COMPENSATION.name}
                defaultValue={
                  defaultValues[AGENCY_REFERA_DETAILS_FIELDS.WARRANTY_COMPENSATION.name]
                }
                render={props => (
                  <SwitchComponent
                    checked={props.value}
                    onChange={e => props.onChange(e.target.checked)}
                  />
                )}
              />
            </div>
            <div className={styles.switchInputGroup}>
              <label
                className={styles.switchLabel}
                htmlFor={AGENCY_REFERA_DETAILS_FIELDS.PILOT_AUTO_TICKET.name}
              >
                Pilot Auto Ticket
              </label>
              <Controller
                control={methods.control}
                name={AGENCY_REFERA_DETAILS_FIELDS.PILOT_AUTO_TICKET.name}
                defaultValue={defaultValues[AGENCY_REFERA_DETAILS_FIELDS.PILOT_AUTO_TICKET.name]}
                render={props => (
                  <SwitchComponent
                    checked={props.value}
                    onChange={e => props.onChange(e.target.checked)}
                  />
                )}
              />
            </div>
            <div className={styles.row} />
            <div style={{ gridColumn: 'span 3' }}>
              <div style={{ width: '100%' }}>
                <CheckedMultiSelect
                  control={control}
                  name="flows"
                  label="Fluxo"
                  defaultValue={agency?.flows?.map(flow => ({
                    value: flow.name,
                    label: flow.description,
                  }))}
                  options={flowOptions}
                />
              </div>
            </div>
          </Accordion>
          <Accordion title="Ativação de Funcionalidades" className={styles.accordion}>
            <div className={styles.switchInputGroup}>
              <ControlledSwitch
                label={
                  <div className={styles.switchRentLabel}>
                    IAs de Triagem
                    <Tooltip
                      title={
                        <>
                          Proibido em SAAS.
                          <br />
                          Se selecionado, as IAs de triagem irão sugerir informações nos campos de
                          triagem.
                        </>
                      }
                      arrow
                    >
                      <HelpOutlineOutlinedIcon className={styles.tooltipIcon} />
                    </Tooltip>
                  </div>
                }
                name={AGENCY_REFERA_DETAILS_FIELDS.TRIAGE_AIS.name}
                disabled={watchContractType === 'SAAS'}
                containerClasses={styles.checkboxContainer}
              />
            </div>
            <div className={styles.switchInputGroup}>
              <ControlledSwitch
                label={
                  <div className={styles.switchRentLabel}>
                    Triagem automática
                    <Tooltip
                      title="Proibido em SAAS. Faz com que a IA preencha não só os campos de sugestão, mas também os campos da triagem."
                      arrow
                    >
                      <HelpOutlineOutlinedIcon className={styles.tooltipIcon} />
                    </Tooltip>
                  </div>
                }
                key={watchContractType}
                name={AGENCY_REFERA_DETAILS_FIELDS.AUTO_TRIAGE.name}
                disabled={watchContractType === 'SAAS'}
                customOnChange={handleAutoTriageChange}
                containerClasses={styles.checkboxContainer}
              />
            </div>
            <div className={styles.switchInputGroup}>
              <ControlledSwitch
                label={
                  <div className={styles.switchRentLabel}>
                    Mensageria de Pool
                    <Tooltip
                      title="Se assinalado, os seus chamados também vão gerar mensagens de whatsapp, aos prestadores da Refera, sobre novo Pool."
                      arrow
                    >
                      <HelpOutlineOutlinedIcon className={styles.tooltipIcon} />
                    </Tooltip>
                  </div>
                }
                key="use-pool-for-whatsapp"
                name={AGENCY_REFERA_DETAILS_FIELDS.USE_POOL_FOR_WHATSAPP.name}
                defaultValue={
                  defaultValues[AGENCY_REFERA_DETAILS_FIELDS.USE_POOL_FOR_WHATSAPP.name]
                }
                containerClasses={styles.checkboxContainer}
              />
            </div>
            <div className={styles.switchInputGroup}>
              <ControlledSwitch
                label={
                  <div className={styles.switchRentLabel}>
                    Mensageria automática - SAAS
                    <Tooltip
                      title="Se assinalado, você está com o envio de mensageria automática e manual ativo"
                      arrow
                    >
                      <HelpOutlineOutlinedIcon className={styles.tooltipIcon} />
                    </Tooltip>
                  </div>
                }
                key="auto-messaging-saas"
                name={AGENCY_REFERA_DETAILS_FIELDS.AUTO_MESSAGING_SAAS.name}
                defaultValue={defaultValues[AGENCY_REFERA_DETAILS_FIELDS.AUTO_MESSAGING_SAAS.name]}
                disabled={watchContractType !== 'SAAS'}
                containerClasses={styles.checkboxContainer}
              />
            </div>

            <div className={styles.switchInputGroup}>
              <ControlledSwitch
                label={
                  <div className={styles.switchRentLabel}>
                    IA de Correção de Texto
                    <Tooltip
                      title=" Se selecionado, as IAs de Correção de texto ficarão disponíveis para a intermediária."
                      arrow
                    >
                      <HelpOutlineOutlinedIcon className={styles.tooltipIcon} />
                    </Tooltip>
                  </div>
                }
                key={watchContractType}
                name={AGENCY_REFERA_DETAILS_FIELDS.AI_TEXT_CORRECTION.name}
                defaultValue={defaultValues[AGENCY_REFERA_DETAILS_FIELDS.AI_TEXT_CORRECTION.name]}
                containerClasses={styles.checkboxContainer}
              />
            </div>
            <div className={styles.switchInputGroup}>
              <ControlledSwitch
                label={
                  <div className={styles.switchRentLabel}>
                    IA de Conferência de preço
                    <Tooltip
                      title=" Se selecionado, as IAs de Conferência de preço ficarão disponíveis para a intermediária."
                      arrow
                    >
                      <HelpOutlineOutlinedIcon className={styles.tooltipIcon} />
                    </Tooltip>
                  </div>
                }
                key={watchContractType}
                name={AGENCY_REFERA_DETAILS_FIELDS.AI_PRICE_CONFERENCE.name}
                defaultValue={defaultValues[AGENCY_REFERA_DETAILS_FIELDS.AI_PRICE_CONFERENCE.name]}
                containerClasses={styles.checkboxContainer}
              />
            </div>
            <div className={styles.switchInputGroup}>
              <ControlledSwitch
                label={
                  <div className={styles.switchRentLabel}>
                    Garantia estendida
                    <Tooltip
                      title="Proibido em SAAS. Quando habilitado, a funcionalidade de garantia estendida ficará disponível para a intermediária."
                      arrow
                    >
                      <HelpOutlineOutlinedIcon className={styles.tooltipIcon} />
                    </Tooltip>
                  </div>
                }
                name={AGENCY_REFERA_DETAILS_FIELDS.USE_EXTENDED_WARRANTY.name}
                disabled={watchContractType === 'SAAS'}
                defaultValue={
                  defaultValues[AGENCY_REFERA_DETAILS_FIELDS.USE_EXTENDED_WARRANTY.name]
                }
                containerClasses={styles.checkboxContainer}
              />
            </div>
          </Accordion>
          {watchAreaOfActivity === 'Garantidora' && (
            <Accordion title="Lista de Exceções por cidade" className={styles.accordion}>
              <div className={styles.regionList}>
                {sortedCityExceptionFields?.map((cityException, index) => (
                  <AccordionInfo
                    key={`${cityException.id || cityException.city}-${cityException.originalIndex}`}
                    isFirst={index === 0}
                    type="region"
                    values={cityException}
                    isEditing
                    handleDeleteValue={() => handleDeleteCityException(cityException.originalIndex)}
                    userEdit={() => openEditCityExceptionModal(cityException.originalIndex)}
                  />
                ))}
              </div>
              <Button
                className={styles.addRegionButton}
                variant="contained"
                color="primary"
                onClick={openAddCityExceptionModal}
              >
                + Adicionar registro
              </Button>

              {isCityExceptionsModalOpen && (
                <ServiceAreasModal
                  modalOpen={isCityExceptionsModalOpen}
                  handleModal={handleCloseCityExceptionModal}
                  handleSubmit={handleSubmitCityException}
                  cityOptions={cityOptions}
                  stateOptions={cityExceptionStateOptions}
                  stateNames={cityExceptionStateNames}
                  regionsList={cityExceptions}
                />
              )}
            </Accordion>
          )}
        </Grid>
      </form>
    </FormProvider>
  )
}

export default ReferaDataScreen
